import React, { useState } from 'react';
import type { MouseEvent } from 'react';
import { Button, Menu, MenuItem } from '@mui/material';
import { KeyboardArrowDown as KeyboardArrowDownIcon } from '@mui/icons-material';
import {
  MANAGER_APPLICATION_ACTIONS_BUTTON_LABEL,
  MANAGER_APPLICATION_ACTION_ASSIGN_LABEL,
  MANAGER_APPLICATION_ACTION_COVENANTS_LABEL,
  MANAGER_APPLICATION_ACTION_IN_PROGRESS_LABEL,
  MANAGER_APPLICATION_COVENANTS_ALLOWED_STATUSES,
  MANAGER_FINAL_STATUSES,
  MANAGER_APPLICATION_ACTION_UNASSIGN_LABEL,
  MANAGER_APPLICATION_ACTION_VIEW_LABEL,
} from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/constants.ts';
import type { TManagerApplicationRow } from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/types.ts';
import { dataFormStyles } from '~/modules/covenants/CovenantsDataForm/styles.ts';
import { managerComponentStyles } from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/components/styles.ts';

interface IManagerApplicationActionsMenuProps {
  application: TManagerApplicationRow;
  isAssignedToCurrentManager: boolean;
  activeAssignmentAction: {
    applicationId: string;
    action: 'assign' | 'unassign';
  } | null;
  onAssignApplication: (application: TManagerApplicationRow) => void;
  onUnassignApplication: (application: TManagerApplicationRow) => void;
  onViewApplication: (application: TManagerApplicationRow) => void;
  onOpenCovenants: (application: TManagerApplicationRow) => void;
}

export const ActionsMenu: React.FC<IManagerApplicationActionsMenuProps> = ({
  application,
  isAssignedToCurrentManager,
  activeAssignmentAction,
  onAssignApplication,
  onUnassignApplication,
  onViewApplication,
  onOpenCovenants,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isOpen = Boolean(anchorEl);
  const isAssignmentActionDisabled = activeAssignmentAction !== null;
  const isAssignInProgress =
    activeAssignmentAction?.applicationId === application.id &&
    activeAssignmentAction.action === 'assign';
  const isUnassignInProgress =
    activeAssignmentAction?.applicationId === application.id &&
    activeAssignmentAction.action === 'unassign';
  const isFinalStatus = MANAGER_FINAL_STATUSES.has(application.status);
  const isCovenantsAllowedStatus = MANAGER_APPLICATION_COVENANTS_ALLOWED_STATUSES.has(
    application.status
  );

  const handleOpen = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleView = () => {
    handleClose();
    onViewApplication(application);
  };

  const handleAssign = () => {
    handleClose();
    onAssignApplication(application);
  };

  const handleOpenCovenants = () => {
    handleClose();
    onOpenCovenants(application);
  };

  const handleUnassign = () => {
    handleClose();
    onUnassignApplication(application);
  };

  return (
    <>
      <Button
        variant="contained"
        size="small"
        endIcon={<KeyboardArrowDownIcon fontSize="small" />}
        onClick={handleOpen}
        sx={managerComponentStyles.tableActionBtn}
        aria-controls={isOpen ? `manager-application-actions-menu-${application.id}` : undefined}
        aria-haspopup="true"
        aria-expanded={isOpen ? 'true' : undefined}
      >
        {MANAGER_APPLICATION_ACTIONS_BUTTON_LABEL}
      </Button>
      <Menu
        id={`manager-application-actions-menu-${application.id}`}
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleClose}
        keepMounted
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        slotProps={{
          paper: {
            sx: dataFormStyles.filterMenuPaper,
          },
        }}
      >
        <MenuItem onClick={handleView} sx={dataFormStyles.filterMenuItem}>
          {MANAGER_APPLICATION_ACTION_VIEW_LABEL}
        </MenuItem>
        {isCovenantsAllowedStatus && (
          <MenuItem onClick={handleOpenCovenants} sx={dataFormStyles.filterMenuItem}>
            {MANAGER_APPLICATION_ACTION_COVENANTS_LABEL}
          </MenuItem>
        )}
        {!application.assignee && (
          <MenuItem
            onClick={handleAssign}
            disabled={isAssignmentActionDisabled}
            sx={dataFormStyles.filterMenuItem}
          >
            {isAssignInProgress
              ? MANAGER_APPLICATION_ACTION_IN_PROGRESS_LABEL
              : MANAGER_APPLICATION_ACTION_ASSIGN_LABEL}
          </MenuItem>
        )}
        {application.assignee && isAssignedToCurrentManager && (
          <MenuItem
            onClick={handleUnassign}
            disabled={isAssignmentActionDisabled || isFinalStatus}
            sx={dataFormStyles.filterMenuItem}
          >
            {isUnassignInProgress
              ? MANAGER_APPLICATION_ACTION_IN_PROGRESS_LABEL
              : MANAGER_APPLICATION_ACTION_UNASSIGN_LABEL}
          </MenuItem>
        )}
      </Menu>
    </>
  );
};


